/*
* Created on Jan 22, 2021
* @author: Adam Cheng <adamcheng@qnap.com>
*/
import React from 'react';
import ic_edit_svg from "../../static/img/utils/ic-edit.svg";
import {useTimezone} from "../../service/timezone";
import {dataCellLabelToDataGridArea} from "../../service/utils";
import {findAdminMapEntry} from "./siteAdminUtils";
import {useOrganization} from "../../context/OrganizationContext";
import {isSiteAdmin} from "../../service/adminMembership";

export const resolveAdminLabel = (entry, map) => {
    if (!entry) {
        return null;
    }

    if (typeof entry === 'string') {
        const mappedEntry = findAdminMapEntry(map, entry);
        if (!mappedEntry) {
            return null;
        }
        return mappedEntry.name || mappedEntry.display_name || mappedEntry.email || mappedEntry.qid || null;
    }

    if (typeof entry === 'object') {
        return entry.name || entry.display_name || entry.email || entry.qid || null;
    }

    return null;
};

export const flattenSiteAdmins = (admin, userMap, groupMap) => {
    const isOrgOwner = (u) => u?.is_owner || u?.role === 'owner';
    const users = (admin?.users || [])
        .map((userEntry) => {
            const resolved = typeof userEntry === 'string'
                ? findAdminMapEntry(userMap, userEntry)
                : (typeof userEntry === 'object' ? userEntry : null);
            if (!resolved || isOrgOwner(resolved)) return null;
            return resolveAdminLabel(userEntry, userMap);
        })
        .filter(Boolean);
    const groups = admin?.groups?.map((groupEntry) => resolveAdminLabel(groupEntry, groupMap)) || [];
    return [...users, ...groups].filter(Boolean);
};

const SiteTableRowComponent = ({ row_data, index, doRefresh, disPatcher, translate:t, isOwner, show_checkbox=false, table_head_fields, state}) => {
    const {userProfile} = useOrganization();
    const {is_owner} = userProfile();
    const isUserManager = state?.userPermissions?.role === 'user_manager';
    const currentUser = JSON.parse(window.localStorage.getItem('currentUser'));
    const hasPermission = !isUserManager && (is_owner || isSiteAdmin(currentUser?.user_id, row_data, state?.groups));
    const toggle_checkbox = (row) => {
        disPatcher({type:'toggle_checkbox', payload: row});
    }

    const onClickOpenModal = (data) => {
        disPatcher({type:'open_edit_modal', payload: data});
    }

    const flattenAdmins = () => {
        return flattenSiteAdmins(row_data?.admin, state?.user_map, state?.group_map);
    }

     const onOpenStandaloneAdmin = () => {
        disPatcher({type: 'toggle_standalone_admin', payload: {show_admin: true, row_data}});
    }

    // useTimezone is a hook — call unconditionally at the top, even if the
    // created_at cell ends up filtered out for non-owners (it isn't, but the
    // rules-of-hooks lint still applies).
    const createdAtLabel = useTimezone(row_data.created_at);
    // Look up fields by id rather than fixed indices so the table works
    // whether the column set is full (owner) or trimmed (non-owner —
    // administrator + action columns hidden per TEM61510-22480 spec).
    const fieldById = (id) => table_head_fields.find(f => f.field_id === id);
    const nameField = fieldById('name');
    const descField = fieldById('description');
    const adminField = fieldById('administrator');
    const createdField = fieldById('created_at');
    const actionField = fieldById('action');
    return (
        <div className="flexbox-trow" key={index}>
            <div className="tbody-cell select-cell">
                <div className={`comp-checkbox no-label ${show_checkbox ? 'visible-checkbox' : ''}`}>
                    {row_data?.default ?
                        <input type="checkbox" disabled={true}/> :
                        <input type="checkbox" id={`cb-${index}`} checked={row_data.checked || false} onChange={() => toggle_checkbox(row_data)} disabled={!is_owner}/>
                    }
                    <label htmlFor={`cb-${index}`} className="comp-label"> </label>
                </div>
            </div>
            {nameField &&
                <div className={`tbody-cell ${nameField.width_style}`}
                     data-cell-label={t(nameField.field_name)}
                     data-grid-area={dataCellLabelToDataGridArea(nameField.field_name)}>
                    <div className="default-site">
                        <div className="site-name">{row_data.name}</div>
                        {row_data.default && <div className="mark">{t('@Default Site@')}</div>}
                    </div>
                </div>
            }
            {descField &&
                <div className={`tbody-cell ${descField.width_style}`}
                     data-cell-label={t(descField.field_name)}
                     data-grid-area={dataCellLabelToDataGridArea(descField.field_name)}>
                    {row_data.description}
                </div>
            }
            {adminField &&
                <div className={`tbody-cell ${adminField.width_style}`}
                     data-cell-label={t(adminField.field_name)}
                     data-grid-area={dataCellLabelToDataGridArea(adminField.field_name)}>
                    <div className="tag-group">
                        {flattenAdmins()?.length > 0 && flattenAdmins().slice(0, 2).map((admin, index) => (
                            <span className="tag" key={index}>{admin}</span>
                        ))}
                        {flattenAdmins()?.length > 2 &&
                            <span className="tag tag-more">{`+ ${flattenAdmins()?.length - 2}`}</span>
                        }
                        {is_owner &&
                            <button className="q-btn q-btn-default q-btn-tiny q-btn-rounded-full tag-act--add" onClick={onOpenStandaloneAdmin}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10 5V15" stroke="#000" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"></path>
                                    <path d="M5 10H15" stroke="#000" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"></path>
                                </svg>
                            </button>
                        }
                        <div className="popover read-only group-tooltip">
                            <ul className="popover-menu">
                                <li className="section">
                                    {
                                        flattenAdmins()?.map((admin, idx) => (<div className="section-items" key={`${idx}-${admin}`}>{admin}</div>))
                                    }
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            }
            {createdField &&
                <div className={`tbody-cell ${createdField.width_style}`}
                     data-cell-label={t(createdField.field_name)}
                     data-grid-area={dataCellLabelToDataGridArea(createdField.field_name)}>
                    {createdAtLabel}
                </div>
            }
            {actionField &&
                <div className={`tbody-cell ${actionField.width_style}`}
                     data-cell-label={t(actionField.field_name)}
                     data-grid-area={dataCellLabelToDataGridArea(actionField.field_name)}>
                    <div className="act-btn-group">
                        {is_owner && hasPermission &&
                            <div className="act-btn" onClick={() => onClickOpenModal(row_data)}>
                                <img src={ic_edit_svg} alt=""/>
                            </div>
                        }
                    </div>
                </div>
            }
        </div>
    );
};

export default SiteTableRowComponent;
